import { message } from 'antd';

export interface ExportField {
	key: string;
	label: string;
}

// One exported column, in export order. `label` is only kept for a column the user renamed.
export interface ExportColumn {
	key: string;
	label?: string;
}

export interface ExportTemplate {
	id?: number;
	name: string;
	entityType: string;
	columns: ExportColumn[];
	fileFormat: string;
	isPublic: boolean;
	isPinned: boolean;
	createdBy?: string;
}

export interface ExportTemplatesState {
	records: { [id: number]: ExportTemplate };
}

export interface ExportViewer {
	email: string;
	roles?: string[];
}

export type ExportFilters = { [key: string]: unknown };

export type ExportRow = { [key: string]: unknown };

export const DEFAULT_FILE_FORMAT = 'csv';

export const FILE_FORMATS = [
	{ key: 'csv', label: 'CSV (.csv)', suffix: '(.csv)' },
	{ key: 'xlsx', label: 'Excel (.xlsx)', suffix: '(Excel)' },
];

export const fileFormatSuffix = (fileFormat: string): string => {
	const format = FILE_FORMATS.find((candidate) => candidate.key === fileFormat);
	return format ? format.suffix : '';
};

export const showExportQueued = (queuedMessage: string) => {
	const hide = message.loading(queuedMessage, 0);
	setTimeout(hide, 7000);
};

export const defaultColumns = (fields: ExportField[]): ExportColumn[] =>
	fields.map((field) => ({ key: field.key }));

const defaultLabelOf = (fields: ExportField[], key: string): string => {
	const field = fields.find((candidate) => candidate.key === key);
	return field ? field.label : key;
};

export const columnLabel = (column: ExportColumn, fields: ExportField[]): string =>
	column.label !== undefined ? column.label : defaultLabelOf(fields, column.key);

export const isRenamed = (column: ExportColumn, fields: ExportField[]): boolean =>
	column.label !== undefined &&
	column.label.trim() !== '' &&
	column.label.trim() !== defaultLabelOf(fields, column.key);

// What is saved and exported: renamed columns carry their trimmed label, the rest fall back to the default.
export const normalizedColumns = (columns: ExportColumn[], fields: ExportField[]): ExportColumn[] =>
	columns.map((column) =>
		isRenamed(column, fields)
			? { key: column.key, label: column.label.trim() }
			: { key: column.key }
	);

// Drops the template columns this viewer can no longer export.
export const templateColumns = (template: ExportTemplate, fields: ExportField[]): ExportColumn[] =>
	template.columns.filter((column) => fields.some((field) => field.key === column.key));

export const sameColumns = (
	columns: ExportColumn[],
	otherColumns: ExportColumn[],
	fields: ExportField[]
): boolean =>
	JSON.stringify(normalizedColumns(columns, fields)) ===
	JSON.stringify(normalizedColumns(otherColumns, fields));

export const toggleColumn = (columns: ExportColumn[], key: string): ExportColumn[] =>
	columns.some((column) => column.key === key)
		? columns.filter((column) => column.key !== key)
		: [...columns, { key }];

// Keeps the current order and names, and appends the missing fields in catalog order.
export const selectAllColumns = (
	columns: ExportColumn[],
	fields: ExportField[]
): ExportColumn[] => [
	...columns,
	...fields
		.filter((field) => !columns.some((column) => column.key === field.key))
		.map((field) => ({ key: field.key })),
];

export const moveColumn = (
	columns: ExportColumn[],
	fromIndex: number,
	toIndex: number
): ExportColumn[] => {
	const reordered = [...columns];
	const [moved] = reordered.splice(fromIndex, 1);
	reordered.splice(toIndex, 0, moved);
	return reordered;
};

export const renameColumn = (columns: ExportColumn[], key: string, label: string): ExportColumn[] =>
	columns.map((column) => (column.key === key ? { key, label } : column));

// A blank name falls back to the default label.
export const settleColumnLabel = (columns: ExportColumn[], key: string): ExportColumn[] =>
	columns.map((column) =>
		column.key === key && column.label !== undefined && column.label.trim() === ''
			? { key }
			: column
	);

export const matchesSearch = (
	field: ExportField,
	column: ExportColumn | undefined,
	searchText: string
): boolean => {
	const needle = searchText.trim().toLowerCase();
	if (!needle) {
		return true;
	}
	const names = [field.label, column && column.label ? column.label : ''];
	return names.some((name) => name.toLowerCase().indexOf(needle) >= 0);
};

// Dotted keys read nested fields, exactly as the report generator writes them.
export const valueAtPath = (row: ExportRow, key: string): unknown =>
	key
		.split('.')
		.reduce<unknown>(
			(value, segment) =>
				value === null || value === undefined ? undefined : (value as ExportRow)[segment],
			row
		);

// Spreadsheet column letters: 0 -> A, 25 -> Z, 26 -> AA.
export const spreadsheetColumnName = (index: number): string =>
	(index >= 26 ? spreadsheetColumnName(Math.floor(index / 26) - 1) : '') +
	String.fromCharCode(65 + (index % 26));

// Only the author can change, pin or delete a template, and a public one only while they are an admin.
export const canEditTemplate = (
	template: ExportTemplate,
	email: string,
	isAdmin: boolean
): boolean =>
	(template.createdBy || '').toLowerCase() === (email || '').toLowerCase() &&
	(!template.isPublic || isAdmin);
